import { decodeJWT } from 'aws-amplify/auth';

import { AMPLIFY_API } from '../config-global';
import { nativeInfo, isNativeApp, sendToNative } from './native-bridge';

export const usesNativeAuth = () => isNativeApp() && (nativeInfo()?.bridgeVersion >= 5 || /HeyJobNative\/5\b/.test(navigator.userAgent));
const authError = (reason) => Object.assign(new Error(reason === 'unauthenticated' ? 'Accesso necessario.' : 'Accesso non disponibile. Riprova.'), { name: reason === 'unauthenticated' ? 'UserUnAuthenticatedException' : 'NativeAuthError' });

let sequence = 0;
let pending;
let pendingForced = false;
let stopped = false;
const subscribers = new Set();

function requestSession(forceRefresh) {
  if (stopped) return Promise.reject(authError('unauthenticated'));
  const info = nativeInfo();
  if (!info?.capabilities?.nativeAuth || !info.documentId || !Number.isSafeInteger(info.authGeneration)) return Promise.reject(authError('unavailable'));
  sequence += 1;
  const requestId = `auth-${sequence}`;
  return new Promise((resolve, reject) => {
    let timer;
    const cleanup = () => { clearTimeout(timer); window.removeEventListener('heyjobNativeMessage', receive); };
    const receive = ({ detail }) => {
      if (detail?.type === 'AUTH_STATE_CHANGED' && detail.generation === info.authGeneration && !detail.authenticated) {
        cleanup(); reject(authError('unauthenticated')); return;
      }
      if (detail?.requestId !== requestId || detail.documentId !== info.documentId || detail.generation !== info.authGeneration) return;
      if (detail.type !== 'AUTH_SESSION' && detail.type !== 'AUTH_ERROR') return;
      cleanup();
      if (detail.type === 'AUTH_ERROR') { reject(authError(detail.reason)); return; }
      try {
        if (typeof detail.idToken !== 'string' || typeof detail.accessToken !== 'string' || detail.expiresAt * 1000 <= Date.now()) throw authError('unavailable');
        const tokens = { idToken: decodeJWT(detail.idToken), accessToken: decodeJWT(detail.accessToken) };
        if (tokens.idToken.payload.sub !== tokens.accessToken.payload.sub || tokens.idToken.payload.token_use !== 'id' || tokens.accessToken.payload.token_use !== 'access') throw authError('unavailable');
        const issuer = `https://cognito-idp.${AMPLIFY_API.userPoolId.split('_')[0]}.amazonaws.com/${AMPLIFY_API.userPoolId}`;
        if (tokens.idToken.payload.iss !== issuer || tokens.accessToken.payload.iss !== issuer
          || tokens.idToken.payload.aud !== AMPLIFY_API.userPoolWebClientId || tokens.accessToken.payload.client_id !== AMPLIFY_API.userPoolWebClientId) throw authError('unavailable');
        if (stopped) throw authError('unauthenticated');
        resolve(tokens);
      } catch { reject(authError('unavailable')); }
    };
    window.addEventListener('heyjobNativeMessage', receive);
    timer = setTimeout(() => { cleanup(); reject(authError('unavailable')); }, 20000);
    sendToNative({ type: 'AUTH_GET_SESSION', requestId, documentId: info.documentId, generation: info.authGeneration, forceRefresh });
  });
}

// No persistent web token store. Amplify callers share only the in-flight request.
export const nativeTokenProvider = {
  getTokens({ forceRefresh = false } = {}) {
    if (pending && forceRefresh && !pendingForced) return pending.then(() => nativeTokenProvider.getTokens({ forceRefresh: true }));
    if (!pending) {
      pendingForced = forceRefresh;
      pending = requestSession(forceRefresh).finally(() => { pending = undefined; });
    }
    return pending;
  },
};

export function subscribeNativeAuth(listener) {
  subscribers.add(listener);
  const receive = ({ detail }) => {
    if (detail?.type !== 'AUTH_STATE_CHANGED' || detail.generation !== nativeInfo()?.authGeneration || detail.authenticated) return;
    stopped = true;
    listener();
  };
  window.addEventListener('heyjobNativeMessage', receive);
  return () => { subscribers.delete(listener); window.removeEventListener('heyjobNativeMessage', receive); };
}

export function nativeSignOut() {
  sequence += 1;
  stopped = true;
  subscribers.forEach(listener => listener());
  const info = nativeInfo();
  sendToNative({ type: 'AUTH_SIGN_OUT', requestId: `auth-${sequence}`, documentId: info?.documentId, generation: info?.authGeneration });
}

export function nativeWebReady() {
  sequence += 1;
  const info = nativeInfo();
  sendToNative({ type: 'AUTH_WEB_READY', requestId: `auth-${sequence}`, documentId: info?.documentId, generation: info?.authGeneration });
}
